import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreorderScreen = () => {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cart, setCart] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [products, setProducts] = useState([]);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Fetch products from backend once on mount
  useEffect(() => {
    axios.get(`${API}/products`)
      .then(res => setProducts(res.data || []))
      .catch(err => console.error('Error loading products:', err));
  }, []);

  // Get unique brands and suppliers
  const brands = useMemo(
    () => [...new Set(products.map(p => p.brand).filter(Boolean))].sort(),
    [products]
  );
  const suppliers = useMemo(
    () => [...new Set(products.map(p => p.supplier).filter(Boolean))].sort(),
    [products]
  );

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, [cart]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (value.length >= 2 || selectedBrand || selectedSupplier) {
      // Search by UPC or description with brand/supplier filters
      let filtered = products;

      // Apply brand filter
      if (selectedBrand) {
        filtered = filtered.filter(p => p.brand === selectedBrand);
      }

      // Apply supplier filter
      if (selectedSupplier) {
        filtered = filtered.filter(p => p.supplier === selectedSupplier);
      }

      // Apply text search
      if (value.length >= 2) {
        filtered = filtered.filter(p => 
          p.upc.toLowerCase().includes(value.toLowerCase()) ||
          p.description.toLowerCase().includes(value.toLowerCase())
        );
      }

      filtered = filtered.slice(0, 10); // Limit to 10 suggestions
      
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleBrandChange = (e) => {
    setSelectedBrand(e.target.value);
    // Trigger search update
    setTimeout(() => {
      if (searchInput.length >= 2 || e.target.value || selectedSupplier) {
        handleSearchChange({ target: { value: searchInput } });
      }
    }, 0);
  };

  const handleSupplierChange = (e) => {
    setSelectedSupplier(e.target.value);
    // Trigger search update
    setTimeout(() => {
      if (searchInput.length >= 2 || selectedBrand || e.target.value) {
        handleSearchChange({ target: { value: searchInput } });
      }
    }, 0);
  };

  const clearFilters = () => {
    setSelectedBrand('');
    setSelectedSupplier('');
    setSearchInput('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const selectProduct = (product) => {
    addToCart(product);
    setSearchInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const addToCart = (product) => {
    const existingIndex = cart.findIndex(item => item.description === product.description);
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].qty += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        description: product.description,
        supplier: product.supplier,
        brand: product.brand,
        price: product.price || 0,
        qty: 1
      }]);
    }
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const updateQty = (index, newQty) => {
    if (newQty < 1) return;
    const newCart = [...cart];
    newCart[index].qty = newQty;
    setCart(newCart);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  const handleSavePreorder = async () => {
    if (!phone) {
      alert('Phone number is required');
      return;
    }

    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    try {
      const preorderData = {
        phone: phone.replace(/\D/g, ''),
        name,
        items: cart.map(item => ({
          description: item.description,
          supplier: item.supplier,
          qty: item.qty,
          price: item.price || 0
        }))
      };

      const { data: saved } = await axios.post(`${API}/preorders`, preorderData);

      const total = cart.reduce((sum, item) => sum + (item.price || 0) * item.qty, 0);
      const submittedAt = new Date(saved.created_at || Date.now()).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      });
      alert(
        `✅ Preorder #${saved.order_number} saved!\n` +
        `${submittedAt}\n\n` +
        `📦 Scan items in Lightspeed\n` +
        `💳 Generate Sale on Lightspeed\n` +
        `Preorder Deposit $${total.toFixed(2)}`
      );

      setPhone('');
      setName('');
      setCart([]);
      setSearchInput('');
    } catch (error) {
      console.error('Error saving preorder:', error);
      alert('Failed to save preorder');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Customer Info & Search */}
      <div className="lg:col-span-2 space-y-6">
        {/* Customer Info */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>Customer Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="customer-phone-input"
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="(555) 123-4567"
                maxLength="14"
                className="w-full bg-white border border-gray-300 rounded-lg h-14 px-4 text-lg text-gray-900 placeholder-gray-400 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Customer Name (Optional)
              </label>
              <input
                data-testid="customer-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full bg-white border border-gray-300 rounded-lg h-14 px-4 text-lg text-gray-900 placeholder-gray-400 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Scan/Search with Autocomplete */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>Search Product</h2>
          
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Filter by Brand
              </label>
              <select
                data-testid="brand-filter"
                value={selectedBrand}
                onChange={handleBrandChange}
                className="w-full bg-white border border-gray-300 rounded-lg h-12 px-4 text-base text-gray-900 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 outline-none transition-colors"
              >
                <option value="">All Brands</option>
                {brands.map((brand, index) => (
                  <option key={index} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Filter by Supplier
              </label>
              <select
                data-testid="supplier-filter"
                value={selectedSupplier}
                onChange={handleSupplierChange}
                className="w-full bg-white border border-gray-300 rounded-lg h-12 px-4 text-base text-gray-900 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 outline-none transition-colors"
              >
                <option value="">All Suppliers</option>
                {suppliers.map((supplier, index) => (
                  <option key={index} value={supplier}>{supplier}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(selectedBrand || selectedSupplier) && (
            <div className="mb-4">
              <button
                data-testid="clear-filters-button"
                onClick={clearFilters}
                className="text-sm text-[#0066FF] hover:text-[#0052CC] font-semibold transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}
          
          <div className="relative" ref={suggestionsRef}>
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
            <input
              data-testid="scan-input"
              ref={searchRef}
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              onFocus={() => searchInput.length >= 2 && setShowSuggestions(true)}
              placeholder="Search by name or scan UPC..."
              className="w-full bg-white border border-gray-300 rounded-lg h-14 pl-12 pr-4 text-lg text-gray-900 placeholder-gray-400 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 outline-none transition-colors"
            />
            
            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {suggestions.map((product, index) => (
                  <button
                    key={index}
                    data-testid={`suggestion-${index}`}
                    onClick={() => selectProduct(product)}
                    className="w-full text-left px-4 py-3 hover:bg-[#F9FAFB] border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="font-medium text-gray-900 text-sm">{product.description}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      <span className="font-mono">{product.upc}</span>
                      <span className="mx-2">•</span>
                      <span>{product.brand}</span>
                      <span className="mx-2">•</span>
                      <span>{product.supplier}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {showSuggestions && searchInput.length >= 2 && suggestions.length === 0 && (
              <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                <p className="text-gray-500 text-center">No products found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right: Cart */}
      <div className="lg:col-span-1">
        <div className="bg-white border border-gray-200 rounded-xl p-6 sticky top-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>Cart ({cart.length})</h2>
          
          <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
            {cart.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Cart is empty</p>
            ) : (
              cart.map((item, index) => (
                <div key={index} data-testid={`cart-item-${index}`} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-sm text-gray-900 flex-1">{item.description}</p>
                    <button
                      data-testid={`remove-item-${index}`}
                      onClick={() => removeFromCart(index)}
                      className="text-gray-400 hover:text-red-500 ml-2"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">{item.supplier}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      ${(item.price || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        data-testid={`decrease-qty-${index}`}
                        onClick={() => updateQty(index, item.qty - 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-700 transition-colors"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-semibold">{item.qty}</span>
                      <button
                        data-testid={`increase-qty-${index}`}
                        onClick={() => updateQty(index, item.qty + 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-700 transition-colors"
                      >
                        +
                      </button>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      ${((item.price || 0) * item.qty).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Cart Total */}
          {cart.length > 0 && (
            <div className="border-t border-gray-200 pt-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900" style={{fontFamily: 'Manrope, sans-serif'}}>Total:</span>
                <span className="text-2xl font-bold text-[#0066FF]" style={{fontFamily: 'Manrope, sans-serif'}}>
                  ${cart.reduce((sum, item) => sum + (item.price || 0) * item.qty, 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          <button
            data-testid="save-preorder-button"
            onClick={handleSavePreorder}
            disabled={!phone || cart.length === 0}
            className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold rounded-lg h-14 px-8 text-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">SAVE PREORDER</span>
            <span className="sm:hidden">SAVE</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreorderScreen;